import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import SearchableSelect from "./SearchableSelect";
import {
  isValidEmailFormat,
  normalizeEmail,
  parseEmailsToArray,
} from "../shared/utils/emailParsing";
import { isPlaceholderEmail } from "../shared/utils/displayEmail";
import { resolveEventParticipantInviteEmail } from "../shared/utils/eventNotificationRecipients";
import ErrorBanner from "./ErrorBanner";


import { apiFetch } from "../shared/api/apiFetch";
import { sendSurveyInvites } from "../shared/api/sendSurveyInvites";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { formatEventAttendanceStatusLabel } from "../shared/utils/eventRegistrationDisplay";
import {
  filterParticipantsByAttendance,
  getAttendanceFilterChipOptions,
  getEventAttendanceKey,
} from "../shared/utils/eventAttendanceOptions";

const uiConfig = require("../uiConfig");


function uniqByNormalizedEmail(items) {
  const map = new Map();
  for (const it of items || []) {
    const email = (it && it.email != null ? String(it.email) : "").trim();
    const key = normalizeEmail(email);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        email,
        label: (it && it.label) || email,
        source: (it && it.source) || "manual",
        meta: (it && it.meta) || null,
      });
    }
  }
  return Array.from(map.values());
}

function EmailChip({ email, invalid, onRemove }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "3px 8px",
        borderRadius: "999px",
        border: `1px solid ${invalid ? "#dc3545" : "#ced4da"}`,
        background: invalid ? "#fff5f5" : "#f8f9fa",
        color: invalid ? "#842029" : "#212529",
        fontSize: "12px",
        lineHeight: 1.4,
        margin: "0 6px 6px 0",
        maxWidth: "100%",
      }}
      title={email}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{email}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${email}`}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: invalid ? "#842029" : "#495057",
            fontSize: "14px",
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}

function SegmentedTabs({ value, onChange, options }) {
  return (
    <div
      role="tablist"
      aria-label="Invitation source"
      style={{
        display: "inline-flex",
        gap: "2px",
        padding: "2px",
        borderRadius: "999px",
        border: "1px solid #dee2e6",
        background: "#f8f9fa",
      }}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            style={{
              padding: "6px 12px",
              fontSize: "12px",
              borderRadius: "999px",
              border: "none",
              cursor: "pointer",
              background: active ? "#0d6efd" : "transparent",
              color: active ? "#fff" : "#495057",
              fontWeight: active ? 600 : 500,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ImportPicker({
  selectLabel,
  selectPlaceholder,
  selectSearchPlaceholder,
  options,
  value,
  onValueChange,
  banners,
  emptyHint,
  loading,
  loadingText,
  rows,
  selectedIds,
  setSelectedIds,
  onAddSelected,
  emptyRowsText,
  actionMessage,
  showAttendance = false,
}) {
  const hasSelection = !!String(value || "").trim();
  const fullList = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const [attendanceFilter, setAttendanceFilter] = useState("");
  const headerCheckboxRef = useRef(null);

  useEffect(() => {
    setAttendanceFilter("");
  }, [value, fullList]);

  const attendanceChips = useMemo(
    () => (showAttendance ? getAttendanceFilterChipOptions(fullList) : []),
    [showAttendance, fullList]
  );

  const list = useMemo(
    () =>
      showAttendance ? filterParticipantsByAttendance(fullList, attendanceFilter) : fullList,
    [showAttendance, fullList, attendanceFilter]
  );

  const fullIds = useMemo(
    () => fullList.map((r) => String(r?._id ?? r?.user_id ?? "")).filter(Boolean),
    [fullList]
  );
  const visibleIds = useMemo(
    () => list.map((r) => String(r?._id ?? r?.user_id ?? "")).filter(Boolean),
    [list]
  );

  const selectedCount = useMemo(() => {
    const selectedSet = selectedIds || new Set();
    return fullIds.reduce((acc, id) => (selectedSet.has(id) ? acc + 1 : acc), 0);
  }, [fullIds, selectedIds]);

  const visibleSelectedCount = useMemo(() => {
    const selectedSet = selectedIds || new Set();
    return visibleIds.reduce((acc, id) => (selectedSet.has(id) ? acc + 1 : acc), 0);
  }, [visibleIds, selectedIds]);

  const allSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someSelected = visibleSelectedCount > 0 && !allSelected;

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected, allSelected]);

  function applyAttendanceFilter(nextFilter) {
    const want = nextFilter != null ? String(nextFilter).trim() : "";
    setAttendanceFilter(want);
    if (!want) return;
    const matchingIds = fullList
      .filter((r) => getEventAttendanceKey(r?.attendance) === want)
      .map((r) => String(r?._id ?? r?.user_id ?? ""))
      .filter(Boolean);
    setSelectedIds(new Set(matchingIds));
  }

  return (
    <div style={{ marginTop: "10px", width: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <div style={{ width: "100%", maxWidth: "720px", minWidth: 0, boxSizing: "border-box" }}>
        <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "4px" }}>
          {selectLabel}
        </div>
        <SearchableSelect
          value={value}
          onChange={(v) => onValueChange(v || "")}
          options={Array.isArray(options) ? options : []}
          placeholder={selectPlaceholder}
          searchPlaceholder={selectSearchPlaceholder}
          allowEmpty
          emptyLabel="— Choose…"
          maxVisibleItems={6}
        />
      </div>

      <div style={{ marginTop: "10px" }}>
        {(Array.isArray(banners) ? banners : []).map((b, idx) => (
          <ErrorBanner key={idx} message={b?.message || ""} onDismiss={b?.onDismiss} />
        ))}
      </div>

      {!hasSelection ? (
        <div style={{ marginTop: "12px", fontSize: "13px", color: "#6c757d" }}>{emptyHint}</div>
      ) : loading ? (
        <div style={{ marginTop: "12px", fontSize: "13px", color: "#6c757d" }}>{loadingText}</div>
      ) : (
        <div style={{ marginTop: "12px", width: "100%", minWidth: 0 }}>
          <div
            style={{
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              marginTop: "8px",
              overflow: "hidden",
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "8px 10px",
                background: "#f8f9fa",
                borderBottom: "1px solid #dee2e6",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: "12px", color: "#6c757d", minWidth: 0, flex: "1 1 200px" }}>
                {showAttendance && fullList.length > 0 ? (
                  <div
                    className="entity-label-filter-bar"
                    role="group"
                    aria-label="Filter by attendance"
                    style={{ marginBottom: actionMessage ? "8px" : 0 }}
                  >
                    <div
                      className="entity-label-filter-bar__list"
                      style={{ flexWrap: "wrap", overflowX: "visible" }}
                    >
                      <button
                        type="button"
                        className={`entity-label-filter-chip${attendanceFilter === "" ? " active" : ""}`}
                        onClick={() => setAttendanceFilter("")}
                        aria-pressed={attendanceFilter === ""}
                      >
                        <span className="entity-label-filter-chip__label">All</span>
                        <span className="entity-label-filter-chip__count">({fullList.length})</span>
                      </button>
                      {attendanceChips.map((chip) => {
                        const label = formatEventAttendanceStatusLabel(chip.value);
                        const active = attendanceFilter === chip.value;
                        return (
                          <button
                            key={chip.value}
                            type="button"
                            className={`entity-label-filter-chip${active ? " active" : ""}`}
                            onClick={() => applyAttendanceFilter(chip.value)}
                            aria-pressed={active}
                            title={`Show and select ${label} only`}
                          >
                            <span className="entity-label-filter-chip__label">{label}</span>
                            <span className="entity-label-filter-chip__count">({chip.count})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {actionMessage ? (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      background: "#d1e7dd",
                      border: "1px solid #badbcc",
                      color: "#0f5132",
                      fontWeight: 600,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={actionMessage}
                  >
                    {actionMessage}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onAddSelected}
                disabled={selectedCount === 0}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  borderRadius: "6px",
                  border: selectedCount === 0 ? "1px solid #dee2e6" : "1px solid #0d6efd",
                  background: selectedCount === 0 ? "#e9ecef" : "#0d6efd",
                  color: "#fff",
                  cursor: selectedCount === 0 ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                }}
              >
                {selectedCount > 0 ? `Add selected (${selectedCount})` : "Add selected"}
              </button>
            </div>

            {fullList.length === 0 ? (
              <div style={{ padding: "12px 10px", fontSize: "13px", color: "#6c757d" }}>
                {emptyRowsText}
              </div>
            ) : list.length === 0 ? (
              <div style={{ padding: "12px 10px", fontSize: "13px", color: "#6c757d" }}>
                No participants match this attendance filter.
              </div>
            ) : (
              <div style={{ maxHeight: "260px", overflow: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
                      <th style={{ padding: "8px 10px", width: "40px" }}>
                        <input
                          ref={headerCheckboxRef}
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) setSelectedIds(new Set(visibleIds));
                            else setSelectedIds(new Set());
                          }}
                          aria-label={allSelected ? "Deselect all" : "Select all"}
                        />
                      </th>
                      <th style={{ padding: "8px 10px", textAlign: "left" }}>Name</th>
                      {showAttendance ? (
                        <th style={{ padding: "8px 10px", textAlign: "left", width: "110px" }}>
                          Attendance
                        </th>
                      ) : null}
                      <th style={{ padding: "8px 10px", textAlign: "left" }}>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => {
                      const id = String(r?._id ?? r?.user_id ?? "");
                      const checked = (selectedIds || new Set()).has(id);
                      const name =
                        (r?.label != null ? String(r.label).trim() : "") ||
                        [r?.first_name, r?.last_name].filter(Boolean).join(" ").trim() ||
                        "—";
                      const email = String(r?.email || "").trim();
                      const attendanceLabel = showAttendance
                        ? formatEventAttendanceStatusLabel(r?.attendance)
                        : "";
                      return (
                        <tr key={id} style={{ borderBottom: "1px solid #f1f3f5" }}>
                          <td style={{ padding: "8px 10px" }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = new Set(selectedIds || []);
                                if (e.target.checked) next.add(id);
                                else next.delete(id);
                                setSelectedIds(next);
                              }}
                            />
                          </td>
                          <td style={{ padding: "8px 10px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {name}
                          </td>
                          {showAttendance ? (
                            <td
                              style={{
                                padding: "8px 10px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "#495057",
                              }}
                              title={attendanceLabel}
                            >
                              {attendanceLabel}
                            </td>
                          ) : null}
                          <td style={{ padding: "8px 10px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            {email}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SmartInvitationModal({
  open,
  title,
  account_id,
  survey_id,
  user_id,
  onClose,
  onSent,
}) {
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [activeSource, setActiveSource] = useState("direct"); // direct | import
  const [importMode, setImportMode] = useState("event"); // event | role

  const [directText, setDirectText] = useState("");

  const [staged, setStaged] = useState([]); // [{email,label,source,meta}]
  const [importActionMessage, setImportActionMessage] = useState("");

  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);

  // Events tab state
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [eventResults, setEventResults] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState("");
  const [participants, setParticipants] = useState([]);
  const [participantSelection, setParticipantSelection] = useState(new Set());

  // Roles tab state
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [users, setUsers] = useState([]);
  const [roleUserSelection, setRoleUserSelection] = useState(new Set());
  const [selectedRoleId, setSelectedRoleId] = useState("");

  useEffect(() => {
    if (open) return;
    setActiveSource("direct");
    setImportMode("event");
    setDirectText("");
    setStaged([]);
    setSending(false);
    setEventsError("");
    setEventResults([]);
    setSelectedEventId("");
    setParticipants([]);
    setParticipantsError("");
    setParticipantSelection(new Set());
    setUsers([]);
    setUsersError("");
    setRoleUserSelection(new Set());
    setSelectedRoleId("");
    setImportActionMessage("");
  }, [open]);

  const directParsed = useMemo(() => parseEmailsToArray(directText), [directText]);
  const directChipCandidates = useMemo(
    () =>
      uniqByNormalizedEmail(
        directParsed.map((email) => ({
          email,
          label: email,
          source: "direct",
        }))
      ),
    [directParsed]
  );

  // Keep chips in sync with direct text (so typing/pasting chips emails live)
  useEffect(() => {
    if (!open) return;
    if (activeSource !== "direct") return;
    // We only add/update from direct entry; manual removals happen in staging.
    // Users can still remove from staging (and the textarea stays unchanged).
  }, [open, activeSource]);

  const stagedNormalized = useMemo(() => uniqByNormalizedEmail(staged), [staged]);
  const invalidStaged = useMemo(
    () => stagedNormalized.filter((r) => !isValidEmailFormat(r.email)),
    [stagedNormalized]
  );

  function addToStagingWithStats(recipients) {
    const prev = Array.isArray(staged) ? staged : [];
    const prevKeys = new Set(prev.map((r) => normalizeEmail(r.email)).filter(Boolean));
    const incoming = Array.isArray(recipients) ? recipients : [];

    // Never stage invalid emails; skip them at the boundary.
    const validIncoming = incoming.filter((r) => isValidEmailFormat(r?.email));
    const invalidSkipped = incoming.length - validIncoming.length;

    const incomingKeys = validIncoming.map((r) => normalizeEmail(r.email)).filter(Boolean);
    const incomingUniqueKeys = new Set(incomingKeys);

    const duplicatesWithinIncoming = incomingKeys.length - incomingUniqueKeys.size;
    let duplicatesAlreadyStaged = 0;
    for (const k of incomingUniqueKeys) {
      if (prevKeys.has(k)) duplicatesAlreadyStaged += 1;
    }

    const merged = uniqByNormalizedEmail([...prev, ...validIncoming]);
    setStaged(merged);

    const duplicates = duplicatesWithinIncoming + duplicatesAlreadyStaged;
    const added = Math.max(0, incomingUniqueKeys.size - duplicatesAlreadyStaged);
    return { added, duplicates, invalidSkipped };
  }

  function setImportActionMessageSticky(msg) {
    setImportActionMessage(msg || "");
  }

  function removeFromStaging(email) {
    const key = normalizeEmail(email);
    setStaged((prev) => (prev || []).filter((r) => normalizeEmail(r.email) !== key));
  }

  function submitSend() {
    if (!account_id || !survey_id) return;
    const unique = stagedNormalized;
    if (unique.length === 0) {
      setMessage("Add at least one recipient.");
      setMessageOpen(true);
      return;
    }
    if (invalidStaged.length > 0) {
      setMessage(`Fix ${invalidStaged.length} invalid email(s) before sending.`);
      setMessageOpen(true);
      return;
    }

    setSending(true);
    sendSurveyInvites({
      serverUrl,
      account_id,
      survey_id,
      user_id,
      emails: unique.map((r) => r.email),
    })
      .then((data) => {
        setMessage((data && data.message) || "Invites processed.");
        setMessageOpen(true);
        onSent && onSent(data);
        onClose && onClose();
      })
      .catch((err) => {
        setMessage(getMessageForFetchError(err, "Failed to send invites."));
        setMessageOpen(true);
      })
      .finally(() => setSending(false));
  }

  // --- Import: event sourcing ---
  useEffect(() => {
    if (!open) return;
    if (activeSource !== "import") return;
    if (importMode !== "event") return;
    if (!account_id) return;
    if (eventsLoading) return;
    if (eventResults.length > 0) return;
    setEventsLoading(true);
    setEventsError("");
    // Use all events (not just open) so historical/closed events can be used for invitations.
    apiFetch(serverUrl + "/getEvent", {
      method: "POST",
      body: JSON.stringify({
        account_id,
      }),
    })
      .then((resp) => resp.json())
      .then((data) => setEventResults(Array.isArray(data) ? data : []))
      .catch((err) => {
        setEventResults([]);
        setEventsError(getMessageForFetchError(err));
      })
      .finally(() => setEventsLoading(false));
  }, [open, activeSource, importMode, account_id, serverUrl, eventsLoading, eventResults.length]);

  useEffect(() => {
    if (!open) return;
    if (activeSource !== "import") return;
    if (importMode !== "event") return;
    if (!account_id || !selectedEventId) return;

    setParticipantsLoading(true);
    setParticipantsError("");
    setParticipants([]);
    setParticipantSelection(new Set());

    apiFetch(serverUrl + "/getEventParticipants", {
      method: "POST",
      body: JSON.stringify({ account_id, event_id: selectedEventId }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        // Same parent-fallback as event notifications: placeholder/missing child email → parent.
        const filtered = list
          .map((p) => {
            const email = resolveEventParticipantInviteEmail(p);
            if (!email) return null;
            return { ...p, email };
          })
          .filter(Boolean);
        setParticipants(filtered);
        setParticipantSelection(new Set(filtered.map((p) => String(p._id))));
      })
      .catch((err) => {
        setParticipants([]);
        setParticipantsError(getMessageForFetchError(err));
      })
      .finally(() => setParticipantsLoading(false));
  }, [open, activeSource, importMode, account_id, selectedEventId, serverUrl]);

  function addSelectedParticipants() {
    const selected = new Set(participantSelection || []);
    const toAdd = participants
      .filter((p) => selected.has(String(p._id)))
      .map((p) => {
        const email = (p.email || "").trim();
        const label = [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || email;
        return { email, label, source: "event", meta: { event_id: selectedEventId } };
      });
    const { added, duplicates, invalidSkipped } = addToStagingWithStats(toAdd);
    const parts = [`${added} added`];
    if (duplicates > 0) parts.push(`${duplicates} duplicate${duplicates === 1 ? "" : "s"} skipped`);
    if (invalidSkipped > 0) parts.push(`${invalidSkipped} invalid skipped`);
    setImportActionMessageSticky(parts.join(" · "));
  }

  // --- Import: role sourcing ---
  useEffect(() => {
    if (!open) return;
    if (activeSource !== "import") return;
    if (importMode !== "role") return;
    if (!account_id) return;
    if (users.length > 0 || usersLoading) return;

    setUsersLoading(true);
    setUsersError("");
    apiFetch(serverUrl + "/getUser", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch((err) => {
        setUsers([]);
        setUsersError(getMessageForFetchError(err));
      })
      .finally(() => setUsersLoading(false));
  }, [open, activeSource, importMode, account_id, serverUrl, users.length, usersLoading]);

  const roleOptions = useMemo(() => {
    const counts = new Map();
    for (const u of users || []) {
      const roles = Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []);
      for (const r of roles) {
        const rr = String(r || "").trim();
        if (!rr) continue;
        counts.set(rr, (counts.get(rr) || 0) + 1);
      }
    }
    const list = Array.from(counts.entries())
      .map(([role, count]) => ({ role, count }))
      .sort((a, b) => a.role.localeCompare(b.role));

    // Prefer common functional groups first if present.
    const prefer = ["Tenant", "Vendor", "Lead", "Stakeholder", "Participant", "Parent"];
    const preferSet = new Set(prefer);
    const preferred = list.filter((x) => preferSet.has(x.role));
    const rest = list.filter((x) => !preferSet.has(x.role));
    preferred.sort((a, b) => prefer.indexOf(a.role) - prefer.indexOf(b.role));
    return [...preferred, ...rest];
  }, [users]);

  const roleSelectOptions = useMemo(() => {
    return roleOptions.map((r) => ({
      value: r.role,
      label: `${r.role} (${r.count})`,
      keywords: r.role,
    }));
  }, [roleOptions]);

  const eventSelectOptions = useMemo(() => {
    const list = Array.isArray(eventResults) ? eventResults : [];
    return list
      .map((ev) => {
        const id = String(ev?._id ?? "");
        if (!id) return null;
        const name = (ev?.name || "Event").trim();
        const day = ev?.start_datetime ? new Date(ev.start_datetime) : null;
        const dayLabel = day ? day.toLocaleDateString() : "";
        const organizer = (ev?.organizer_name || "").trim();
        const program = (ev?.program_name || "").trim();
        const count = typeof ev?.participant_count === "number" ? ev.participant_count : null;
        const left = dayLabel ? `${name} - ${dayLabel}` : name;
        const label = count != null ? `${left} (${count})` : left;
        const keywords = [name, dayLabel, organizer, program].filter(Boolean).join(" ");
        return { value: id, label, keywords };
      })
      .filter(Boolean);
  }, [eventResults]);

  const roleMatchedUsers = useMemo(() => {
    const selectedRole = String(selectedRoleId || "").trim();
    if (!selectedRole) return [];
    const rows = [];
    for (const u of users || []) {
      const email = (u.email_id || "").trim();
      if (!email || isPlaceholderEmail(email)) continue;
      const roles = Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []);
      const has = roles.some((r) => String(r || "").trim() === selectedRole);
      if (!has) continue;
      const label =
        [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || email;
      rows.push({
        user_id: String(u._id),
        email,
        label,
      });
    }
    rows.sort((a, b) => a.label.localeCompare(b.label));
    return rows;
  }, [users, selectedRoleId]);

  useEffect(() => {
    // Default role selection checkboxes to "selected" when role matches change
    if (!open || activeSource !== "import" || importMode !== "role") return;
    setRoleUserSelection(new Set(roleMatchedUsers.map((u) => u.user_id)));
  }, [open, activeSource, importMode, roleMatchedUsers]);

  function addSelectedRoleUsers() {
    const selected = new Set(roleUserSelection || []);
    const toAdd = roleMatchedUsers
      .filter((u) => selected.has(u.user_id))
      .map((u) => ({ email: u.email, label: u.label, source: "role", meta: { role: selectedRoleId } }));
    const { added, duplicates, invalidSkipped } = addToStagingWithStats(toAdd);
    const parts = [`${added} added`];
    if (duplicates > 0) parts.push(`${duplicates} duplicate${duplicates === 1 ? "" : "s"} skipped`);
    if (invalidSkipped > 0) parts.push(`${invalidSkipped} invalid skipped`);
    setImportActionMessageSticky(parts.join(" · "));
  }

  if (!open) return null;

  return (
    <>
      <ConfirmationModal
        open={messageOpen}
        title="Send survey"
        message={message}
        mode="alert"
        confirmLabel="OK"
        onConfirm={() => setMessageOpen(false)}
        onCancel={() => setMessageOpen(false)}
      />

      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "12px",
          boxSizing: "border-box",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose && onClose();
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="smart-invite-title"
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "10px",
            maxWidth: "980px",
            width: "100%",
            maxHeight: "82vh",
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            padding: "14px 18px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3
                id="smart-invite-title"
                style={{ margin: "0 0 6px 0", fontSize: "1.1rem", color: "#212529" }}
              >
                {title || "Send survey"}
              </h3>
              <div style={{ fontSize: "12px", color: "#6c757d" }}>
                Add recipients via direct entry or import from events/roles. Duplicates are removed automatically.
              </div>
            </div>
            <SegmentedTabs
              value={activeSource}
              onChange={setActiveSource}
              options={[
                { value: "direct", label: "Direct Entry" },
                { value: "import", label: "Import" },
              ]}
            />
          </div>

          <div style={{ marginTop: "12px" }}>
            {activeSource === "direct" && (
              <div>
                <div style={{ fontSize: "13px", color: "#212529", marginBottom: "8px" }}>
                  Paste or type recipient email addresses. Use commas, semicolons, or new lines. We’ll
                  chip them as you type and highlight invalid formats.
                </div>
                <textarea
                  rows={4}
                  value={directText}
                  onChange={(e) => setDirectText(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 10px",
                    fontSize: "13px",
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "monospace",
                  }}
                  placeholder={"alice@example.org\nbob@example.org\ncarol@example.org"}
                />
                <div style={{ marginTop: "10px" }}>
                  {(directChipCandidates || []).length === 0 ? (
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>No emails yet.</div>
                  ) : (
                    <div>
                      {directChipCandidates.map((r) => (
                        <EmailChip
                          key={normalizeEmail(r.email)}
                          email={r.email}
                          invalid={!isValidEmailFormat(r.email)}
                          onRemove={null}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const { added, duplicates, invalidSkipped } = addToStagingWithStats(directChipCandidates);
                          const parts = [`${added} added`];
                          if (duplicates > 0) parts.push(`${duplicates} duplicate${duplicates === 1 ? "" : "s"} skipped`);
                          if (invalidSkipped > 0) parts.push(`${invalidSkipped} invalid skipped`);
                          setImportActionMessageSticky(parts.join(" · "));
                        }}
                        style={{
                          marginTop: "8px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          borderRadius: "6px",
                          border: "1px solid #0d6efd",
                          background: "#0d6efd",
                          color: "#fff",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Add to queue
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeSource === "import" && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                  <SegmentedTabs
                    value={importMode}
                    onChange={(v) => {
                      const next = v === "role" ? "role" : "event";
                      setImportMode(next);
                      setSelectedEventId("");
                      setParticipants([]);
                      setParticipantsError("");
                      setParticipantSelection(new Set());
                      setRoleUserSelection(new Set());
                      setSelectedRoleId("");
                      setUsersError("");
                    }}
                    options={[
                      { value: "event", label: "Event" },
                      { value: "role", label: "Role" },
                    ]}
                  />
                </div>

                {importMode === "event" && (
                  <ImportPicker
                    selectLabel="Search and select an event"
                    selectPlaceholder={eventsLoading ? "Loading events…" : "Select an event…"}
                    selectSearchPlaceholder="Type to filter events…"
                    options={eventSelectOptions}
                    value={selectedEventId}
                    onValueChange={(v) => setSelectedEventId(v)}
                    banners={[
                      { message: eventsError, onDismiss: () => setEventsError("") },
                      { message: participantsError, onDismiss: () => setParticipantsError("") },
                    ]}
                    emptyHint=""
                    loading={participantsLoading}
                    loadingText="Loading participants…"
                    rows={participants}
                    selectedIds={participantSelection}
                    setSelectedIds={setParticipantSelection}
                    onAddSelected={addSelectedParticipants}
                    emptyRowsText="No participants with usable email addresses found for this event."
                    actionMessage={importActionMessage}
                    showAttendance
                  />
                )}

                {importMode === "role" && (
                  <>
                    <ErrorBanner message={usersError} onDismiss={() => setUsersError("")} />
                    {usersLoading ? (
                      <div style={{ marginTop: "10px", fontSize: "13px", color: "#6c757d" }}>Loading roles…</div>
                    ) : roleOptions.length === 0 ? (
                      <div style={{ marginTop: "10px", fontSize: "13px", color: "#6c757d" }}>
                        No roles found for users in this account.
                      </div>
                    ) : (
                      <ImportPicker
                        selectLabel="Search and select a role"
                        selectPlaceholder="Select a role…"
                        selectSearchPlaceholder="Type to filter roles…"
                        options={roleSelectOptions}
                        value={selectedRoleId}
                        onValueChange={(v) => setSelectedRoleId(String(v || "").trim())}
                        banners={[]}
                        emptyHint=""
                        loading={false}
                        loadingText=""
                        rows={roleMatchedUsers}
                        selectedIds={roleUserSelection}
                        setSelectedIds={setRoleUserSelection}
                        onAddSelected={addSelectedRoleUsers}
                        emptyRowsText="No users found for the selected role."
                        actionMessage={importActionMessage}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Unified Staging Area */}
          <div
            style={{
              marginTop: "14px",
              borderTop: "1px solid #e9ecef",
              paddingTop: "12px",
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              flex: "0 0 auto",
            }}
          >
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "10px",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  borderBottom: "1px solid #e9ecef",
                  background: "#f8f9fa",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#212529" }}>
                    Queue ({stagedNormalized.length})
                  </div>
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    {stagedNormalized.length} recipient{stagedNormalized.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>

              <div style={{ padding: "10px 12px" }}>
              {stagedNormalized.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#6c757d" }}>
                  Your queue is empty. Add recipients from any tab.
                </div>
              ) : (
                <div
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    padding: "10px",
                    maxHeight: "140px",
                    overflow: "auto",
                  }}
                >
                  {stagedNormalized.map((r) => (
                    <EmailChip
                      key={normalizeEmail(r.email)}
                      email={r.email}
                      invalid={!isValidEmailFormat(r.email)}
                      onRemove={() => removeFromStaging(r.email)}
                    />
                  ))}
                </div>
              )}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
                marginTop: "10px",
              }}
            >
              <button
                type="button"
                onClick={() => onClose && onClose()}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  background: "#fff",
                  color: "#495057",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={sending || stagedNormalized.length === 0 || invalidStaged.length > 0}
                onClick={submitSend}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  border: "none",
                  borderRadius: "6px",
                  background:
                    sending || stagedNormalized.length === 0 || invalidStaged.length > 0
                      ? "#adb5bd"
                      : "#0d6efd",
                  color: "#fff",
                  cursor:
                    sending || stagedNormalized.length === 0 || invalidStaged.length > 0
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 700,
                }}
              >
                {sending ? "Sending…" : "Send invites"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

